import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Heading,
  Link,
  Skeleton,
  Spacer,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  DownloadWithLoanProductGQLType,
  UrlAndFilenameGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { PipelinePicker } from './PipelinePicker';
import {
  getBorrowerRecentWaiverUpdatesAggregate,
  waiverImpactHeaderElementId,
} from './utils';
import { VerificationApproveTables } from './VerificationApproveTables';
import { VerificationAssetVerificationSection } from './VerificationAssetVerificationSection';
import { VerificationDownloadExtendedResponseTapesButton } from './VerificationDownloadExtendedResponseTapeButton';
import { useGetReceivableWaiversForCreditFacilityWithOrganizationQuery } from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryValueDifferenceDefinitionModal } from 'packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryValueDifferenceGridItem';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from 'packages/common-frontend/hooks/useUiVisibility';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

type VerificationApproveSectionProps = {
  organizationId: string;
  selectedCreditFacilityId: string;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
  verificationReport: Util.SanitizedJSON;
  verificationReportId: string;
  verificationReportBorrowingBaseReportId: string;
  artifactDocumentId?: string;
  canApproveOrDeny: boolean;
  extendedResponseTapeDownloads: DownloadWithLoanProductGQLType[] | null;
  servicerReportDownload: UrlAndFilenameGQLType | null;
  remediationStatus: {
    remediationRequested: boolean;
    remediationNote: string | null;
  } | null;
};

export const VerificationApproveSection = ({
  organizationId,
  selectedCreditFacilityId,
  selectedReportingDate,
  selectedFileVersion,
  verificationReport,
  verificationReportId,
  verificationReportBorrowingBaseReportId,
  artifactDocumentId,
  canApproveOrDeny,
  extendedResponseTapeDownloads,
  servicerReportDownload,
  remediationStatus,
}: VerificationApproveSectionProps) => {
  const flags = useFeatureFlags();
  // disclosure for the value difference definition help modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId: 'mockCreditFacilityId',
  });

  const {
    data: receivableWaiversData,
    loading: isReceivableWaiversLoading,
  } = useGetReceivableWaiversForCreditFacilityWithOrganizationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId: selectedCreditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId: selectedCreditFacilityId },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: 'Error loading credit agreement.',
      });
    },
  });

  if (
    !receivableWaiversData ||
    isReceivableWaiversLoading ||
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return (
      <Skeleton
        data-testid="loading-verification-approve-accordian"
        height="600px"
      />
    );
  }

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  const parsedCreditAgreement = parseCreditAgreementFullArtifact(
    creditAgreement,
  );

  finleyAssert(
    parsedCreditAgreement,
    `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${selectedCreditFacilityId}] [creditAgreementId: ${creditAgreementId}]`,
  );

  const borrowerOrganizationName =
    parsedCreditAgreement.facilityMechanics.parties.borrowers[0].entity.name; // only one borrower per CA

  const totalBorrowerRecentWaiverUpdates = getBorrowerRecentWaiverUpdatesAggregate(
    {
      receivableWaivers:
        receivableWaiversData.receivableWaiversForCreditFacilityWithOrganization,
      borrowerOrganizationName,
      reportingDate: selectedReportingDate,
    },
  );

  const showBorrowerRecentWaiverUpdatesAlert = Boolean(
    totalBorrowerRecentWaiverUpdates > 0,
  );

  return (
    <>
      <Box mb="40px">
        <Heading mb="20px" as="h4" fontSize="20px">
          Verification Results
        </Heading>
        {showBorrowerRecentWaiverUpdatesAlert && (
          <Alert status="warning" mb="24px">
            <AlertIcon />
            <AlertDescription width="100%">
              <Flex>
                <Text fontWeight={600}>
                  {`${borrowerOrganizationName} updated ${totalBorrowerRecentWaiverUpdates} waivers.`}
                </Text>
                <Spacer />
                <Link
                  href={`#${waiverImpactHeaderElementId}`}
                  color={colors.info}
                  fontWeight={500}
                >
                  View Waiver Impact
                </Link>
              </Flex>
            </AlertDescription>
          </Alert>
        )}
        <PipelinePicker
          organizationId={organizationId}
          selectedReportingDate={selectedReportingDate}
          selectedFileVersion={selectedFileVersion}
          servicerReportDownload={servicerReportDownload}
        />
      </Box>

      {artifactDocumentId &&
        facilityUiVisibility.verification.showAssetVerification && (
          <VerificationAssetVerificationSection
            artifactDocumentId={artifactDocumentId}
            creditFacilityId={selectedCreditFacilityId}
            reportingDate={selectedReportingDate}
            fileVersion={selectedFileVersion}
            adminMode={false}
            onEdit={() => {}}
            dynamicMode // TODO:@asset_verification:  switch to false, when stored mode is ready
          />
        )}

      {remediationStatus?.remediationRequested &&
        remediationStatus.remediationNote && (
          <Alert status="warning" my="40px">
            <AlertIcon alignSelf="flex-start" boxSize="16px" mt="4px" />
            <Box>
              <AlertTitle fontSize="16px" mb="8px">
                Remediation Request Note
              </AlertTitle>
              <AlertDescription>
                {remediationStatus.remediationNote}
              </AlertDescription>
            </Box>
          </Alert>
        )}

      <Flex mb="16px">
        <Heading as="h4" fontSize="20px">
          Calculation Verification
        </Heading>
        <Box onClick={() => onOpen()} ml="8px" cursor="pointer">
          <Icon icon="circle-info" type="regular" color={colors.info} />
        </Box>
        <BorrowingBaseSummaryValueDifferenceDefinitionModal
          isOpen={isOpen}
          onClose={onClose}
        />
        <Spacer />
        {extendedResponseTapeDownloads && (
          <VerificationDownloadExtendedResponseTapesButton
            extendedResponseTapeDownloads={extendedResponseTapeDownloads}
          />
        )}
      </Flex>

      <VerificationApproveTables
        creditFacilityId={selectedCreditFacilityId}
        creditAgreement={parsedCreditAgreement}
        selectedReportingDate={selectedReportingDate}
        fileVersion={selectedFileVersion}
        verificationReport={verificationReport}
        verificationReportId={verificationReportId}
        verificationReportBorrowingBaseReportId={
          verificationReportBorrowingBaseReportId
        }
        artifactDocumentId={artifactDocumentId}
        allReceivableWaivers={
          receivableWaiversData.receivableWaiversForCreditFacilityWithOrganization
        }
        viewMode={canApproveOrDeny ? 'approveDeny' : 'viewOnly'}
      />
    </>
  );
};
